import {
  forwardRef,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { Icon, ICONS } from "./Icon";

/* ── Field wrapper: label + required + error ── */
interface FieldProps {
  label?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, required, error, children, className }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
          {label}
          {required && <span style={{ color: "var(--c-danger)" }}> *</span>}
        </label>
      )}
      {children}
      {error && (
        <span className="flex items-center gap-1 text-xs" style={{ color: "var(--c-danger)" }}>
          <Icon name={ICONS.warning} size={14} />
          {error}
        </span>
      )}
    </div>
  );
}

/** Umumiy input/select uchun bazaviy class + holat style */
const controlClass =
  "h-[52px] w-full rounded-xl px-4 text-base font-semibold outline-none transition-colors placeholder:font-normal";

function controlStyle(invalid?: boolean) {
  return {
    background: "var(--c-surface)",
    color: "var(--c-text)",
    boxShadow: `inset 0 0 0 1px ${invalid ? "var(--c-danger)" : "var(--c-line-input)"}`,
  } as const;
}

/* ── Text input ── */
interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** O'ng tomondagi birlik matni (mas. "So'm") */
  trailing?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { invalid, trailing, className, style, ...rest },
  ref,
) {
  if (trailing != null) {
    return (
      <div className="relative">
        <input
          ref={ref}
          style={{ ...controlStyle(invalid), ...style }}
          className={cn(controlClass, "pr-14 focus:[box-shadow:inset_0_0_0_1px_var(--primary)]", className)}
          {...rest}
        />
        <span
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium"
          style={{ color: "var(--c-text-muted)" }}
        >
          {trailing}
        </span>
      </div>
    );
  }
  return (
    <input
      ref={ref}
      style={{ ...controlStyle(invalid), ...style }}
      className={cn(controlClass, "focus:[box-shadow:inset_0_0_0_1px_var(--primary)]", className)}
      {...rest}
    />
  );
});

/* ── Textarea ── */
interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid, className, style, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      style={{ ...controlStyle(invalid), ...style }}
      className={cn(
        "min-h-[88px] w-full resize-none rounded-xl px-4 py-3 text-base font-medium outline-none transition-colors placeholder:font-normal focus:[box-shadow:inset_0_0_0_1px_var(--primary)]",
        className,
      )}
      {...rest}
    />
  );
});

/* ── Select (native, chevron bilan) ── */
interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  placeholder?: string;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { invalid, placeholder, className, style, children, value, ...rest },
  ref,
) {
  const isEmpty = value === "" || value == null;
  return (
    <div className="relative">
      <select
        ref={ref}
        value={value}
        style={{
          ...controlStyle(invalid),
          color: isEmpty ? "var(--c-text-muted)" : "var(--c-text)",
          ...style,
        }}
        className={cn(
          controlClass,
          "appearance-none pr-11 font-medium focus:[box-shadow:inset_0_0_0_1px_var(--primary)]",
          className,
        )}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <span
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2"
        style={{ color: "var(--c-text-muted)" }}
      >
        <Icon name={ICONS.chevron} size={20} />
      </span>
    </div>
  );
});
